import { defineStore } from 'pinia'
import { type Ref, onScopeDispose, ref, watch } from 'vue'

import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import type { Subscription } from '@/lib/firebase/subscribe'
import { createRetainer } from '@/lib/live/retainer'
import type { BrandScope } from '@/repositories/brand'
import {
  addCompetitor as repoAddCompetitor,
  generateCompetitorAnalysis as repoGenerateCompetitorAnalysis,
  isGenerateAnalysisPreconditionReason,
  removeCompetitor as repoRemoveCompetitor,
  subscribeCompetitorReport as repoSubscribeCompetitorReport,
  subscribeCompetitorReportHistory as repoSubscribeCompetitorReportHistory,
} from '@/repositories/competition'
import {
  refreshSearchSnapshot as repoRefreshSearchSnapshot,
  subscribeLatestSearchSnapshotRun as repoSubscribeLatestSearchSnapshotRun,
} from '@/repositories/search-snapshot'
import { useAuthStore } from '@/stores/auth'
import type { CompetitorReport } from '@/types/competition'
import { failed, loading, type LoadState } from '@/types/load-state'
import type { SearchSnapshotRun } from '@/types/search-snapshot'

export { MAX_TRACKED_COMPETITORS } from '@/types/competition'

export const useCompetitionStore = defineStore('competition', () => {
  const report = ref<LoadState<CompetitorReport>>(loading()) as Ref<LoadState<CompetitorReport>>
  const reportHistory = ref<LoadState<CompetitorReport[]>>(loading()) as Ref<
    LoadState<CompetitorReport[]>
  >
  const refreshing = ref(false)
  const refreshError = ref<string | null>(null)
  /** The precondition reason the last refresh failed with, so the view can pick a specific empty state. */
  const refreshErrorReason = ref<string | null>(null)
  const addingCompetitor = ref(false)
  const addCompetitorError = ref<string | null>(null)
  const removingCompetitors = ref<Set<string>>(new Set())
  const removeCompetitorErrors = ref<Record<string, string>>({})

  const snapshotRun = ref<LoadState<SearchSnapshotRun | null>>(loading()) as Ref<
    LoadState<SearchSnapshotRun | null>
  >

  let reportSub: Subscription<CompetitorReport> | null = null
  let stopReportWatch: (() => void) | null = null
  let reportHistorySub: Subscription<CompetitorReport[]> | null = null
  let stopReportHistoryWatch: (() => void) | null = null
  let snapshotRunSub: Subscription<SearchSnapshotRun | null> | null = null
  let stopSnapshotRunWatch: (() => void) | null = null

  function currentScope(): BrandScope | null {
    const auth = useAuthStore()
    const workspaceId = auth.claims?.workspaceId
    const brandId = auth.claims?.brandId
    if (!workspaceId || !brandId) return null
    return { workspaceId, brandId }
  }

  function openCompetition(): void {
    if (reportSub) return

    const scope = currentScope()
    if (!scope) {
      const message = i18n.global.t('common.noWorkspaceSelected')
      report.value = failed(message)
      reportHistory.value = failed(message)
      snapshotRun.value = failed(message)
      return
    }

    report.value = loading()
    const sub = repoSubscribeCompetitorReport(scope)
    reportSub = sub
    stopReportWatch = watch(
      sub.state,
      (state) => {
        report.value = state
      },
      { immediate: true },
    )

    reportHistory.value = loading()
    const historySub = repoSubscribeCompetitorReportHistory(scope)
    reportHistorySub = historySub
    stopReportHistoryWatch = watch(
      historySub.state,
      (state) => {
        reportHistory.value = state
      },
      { immediate: true },
    )

    snapshotRun.value = loading()
    const runSub = repoSubscribeLatestSearchSnapshotRun(scope)
    snapshotRunSub = runSub
    stopSnapshotRunWatch = watch(
      runSub.state,
      (state) => {
        snapshotRun.value = state
      },
      { immediate: true },
    )
  }

  function closeCompetition(): void {
    reportSub?.stop()
    reportSub = null
    stopReportWatch?.()
    stopReportWatch = null
    report.value = loading()

    reportHistorySub?.stop()
    reportHistorySub = null
    stopReportHistoryWatch?.()
    stopReportHistoryWatch = null
    reportHistory.value = loading()

    snapshotRunSub?.stop()
    snapshotRunSub = null
    stopSnapshotRunWatch?.()
    stopSnapshotRunWatch = null
    snapshotRun.value = loading()
  }

  const competitionRetainer = createRetainer({ open: openCompetition, close: closeCompetition })
  onScopeDispose(competitionRetainer.dispose)

  /** Generates a fresh comparison for the tracked set; the subscription delivers the result. */
  async function refresh(): Promise<void> {
    if (refreshing.value) return

    refreshing.value = true
    refreshError.value = null
    refreshErrorReason.value = null
    try {
      await repoGenerateCompetitorAnalysis()
    } catch (cause) {
      const parsed = parseCallableError(cause)
      refreshErrorReason.value = isGenerateAnalysisPreconditionReason(parsed.reason)
        ? parsed.reason
        : null
      refreshError.value = parsed.message
    } finally {
      refreshing.value = false
    }
  }

  /** Starts a fresh search snapshot; the subscription delivers the run; shares `refreshing` with {@link refresh}. */
  async function refreshSnapshot(): Promise<void> {
    if (refreshing.value) return

    refreshing.value = true
    refreshError.value = null
    refreshErrorReason.value = null
    try {
      await repoRefreshSearchSnapshot()
    } catch (cause) {
      const parsed = parseCallableError(cause)
      refreshErrorReason.value = isGenerateAnalysisPreconditionReason(parsed.reason)
        ? parsed.reason
        : null
      refreshError.value = parsed.message
    } finally {
      refreshing.value = false
    }
  }

  async function addCompetitor(input: { name: string; websiteUrl: string }): Promise<boolean> {
    if (addingCompetitor.value) return false

    addingCompetitor.value = true
    addCompetitorError.value = null
    try {
      await repoAddCompetitor(input)
      return true
    } catch (cause) {
      addCompetitorError.value = parseCallableError(cause).message
      return false
    } finally {
      addingCompetitor.value = false
    }
  }

  /** Removes a tracked competitor; a repeat call for the same competitor while removing is a no-op. */
  async function removeCompetitor(competitorId: string): Promise<void> {
    if (removingCompetitors.value.has(competitorId)) return

    removingCompetitors.value.add(competitorId)
    const errors = { ...removeCompetitorErrors.value }
    delete errors[competitorId]
    removeCompetitorErrors.value = errors
    try {
      await repoRemoveCompetitor({ competitorId })
    } catch (cause) {
      removeCompetitorErrors.value = {
        ...removeCompetitorErrors.value,
        [competitorId]: parseCallableError(cause).message,
      }
    } finally {
      removingCompetitors.value.delete(competitorId)
    }
  }

  return {
    report,
    reportHistory,
    snapshotRun,
    refreshing,
    refreshError,
    refreshErrorReason,
    addingCompetitor,
    addCompetitorError,
    removingCompetitors,
    removeCompetitorErrors,
    retain: competitionRetainer.retain,
    refresh,
    refreshSnapshot,
    addCompetitor,
    removeCompetitor,
  }
})
